import Typography from '@mui/material/Typography';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { checkRut } from 'react-rut-formatter';
import * as Yup from 'yup';
import Stack from '@mui/material/Stack';
import useSnackBars from '../../../../hooks/useSnackBars';
import RutField from '../../../fields/RutField';
import PasswordField from '../../../fields/PasswordField';
import EmailField from '../../../fields/EmailField';
import useUploadInvoiceProviderCredentials from '../../../../hooks/useUploadInvoiceProviderCredentials';
import ErrorBox from '../../../boxes/ErrorBox';
import ShoppingCartStepperNavigation from '../../ShoppingCartStepperNavigation';
import ShoppingCartSummaryActionButton from '../../ShoppingCartSummary/ShoppingCartSummaryActionButton';

// eslint-disable-next-line operator-linebreak
const ERROR_MESSAGE =
  'Uno o más campos son incorrectos. Por favor verifica e intenta nuevamente.';

const VALIDATION_SCHEMA = Yup.object({
  rut: Yup.object({
    raw: Yup.string()
      .required('El RUT es obligatorio')
      .test('isValidRut', 'RUT inválido', (value) => checkRut(value)),
  }),
  email: Yup.string().email('El correo electrónico no es válido'),
  password: Yup.string().required('La contraseña es obligatoria'),
});

const INITIAL_VALUES = {
  rut: { formatted: '', raw: '' },
  email: '',
  password: '',
};

const CredentialProviderCredentialsStep = ({ prevStep, selectedProvider }) => {
  const { addAlert } = useSnackBars();
  const { uploadCredentials, loading } = useUploadInvoiceProviderCredentials();

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      validationSchema={VALIDATION_SCHEMA}
      onSubmit={(values, { setSubmitting, setFieldError }) => {
        uploadCredentials({
          variables: {
            password: values.password,
            naturalPersonRut: values.rut.raw,
            credentialTypeId: selectedProvider.id,
            secondUserName: values.email,
          },
          onCompleted: () => {
            setSubmitting(false);
            addAlert({
              id: 'credential-provider-credentials-success',
              message: 'Credenciales ingresadas exitósamente!',
              severity: 'success',
              color: 'success',
            });
          },
          onError: () => {
            setFieldError('submit', ERROR_MESSAGE);
            setSubmitting(false);
          },
        });
      }}
    >
      {({ errors, isSubmitting }) => (
        <Form style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <Stack direction="column" spacing={2}>

            <RutField
              InputProps={{
                inputProps: { sx: { paddingTop: '0px' } },
                sx: {
                  border: '1px solid',
                  borderColor: 'gray.A400',
                  backgroundColor: 'white',
                  fontSize: '14px',
                },
              }}
            />
            {(selectedProvider?.requiresEmail) && (
            <EmailField
              InputProps={{
                inputProps: { sx: { paddingTop: '0px' } },
                sx: {
                  border: '1px solid',
                  borderColor: 'gray.A400',
                  backgroundColor: 'white',
                  fontSize: '14px',
                },
              }}
            />
            )}
            <PasswordField
              placeholder="Contraseña"
              InputProps={{
                inputProps: { sx: { paddingTop: '0px' } },
                sx: {
                  border: '1px solid',
                  borderColor: 'gray.A400',
                  backgroundColor: 'white',
                  fontSize: '14px',
                },
              }}
            />
            {errors.submit && <ErrorBox>{errors.submit}</ErrorBox>}
            <ShoppingCartStepperNavigation onBack={prevStep}>
              <ShoppingCartSummaryActionButton
                id="confirm-credential-provider-credentials-button"
                type="submit"
                label={(
                  <Typography variant="small_3" color="white" fontWeight={600}>
                    Continuar
                  </Typography>
              )}
                size="small"
                loading={loading || isSubmitting}
                disabled={loading || isSubmitting}
              />
            </ShoppingCartStepperNavigation>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

CredentialProviderCredentialsStep.propTypes = {
  prevStep: PropTypes.func.isRequired,
  selectedProvider: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    iconUrl: PropTypes.string,
    requiresEmail: PropTypes.bool,
  }),
};

CredentialProviderCredentialsStep.defaultProps = {
  selectedProvider: null,
};

export default CredentialProviderCredentialsStep;
